import React, { useCallback, useMemo } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { useUpdateEffect } from '@shared/utils/hooks/useUpdateEffect';
import { RefinementFacetBase } from '../RefinementFacetBase';
import type { RefinementFacetProps, CatalogProductFilterAttribute } from '../../RefinementFacet.types';
import { ProductCatalogAttributeSelectionType } from '@graphql/generated-base';
import type { CatalogProductListFilterAttribute } from '../../../../Catalog.types';
import { ListCheckboxRow, ListCheckboxRowSkeleton } from '../ListCheckboxRow';

export interface FilterItemVo
  extends Readonly<{
      disabled?: boolean;
    }>,
    CatalogProductFilterAttribute {}

export interface RefinementFacetStringListProps extends RefinementFacetProps {}

export const INITIAL_ITEMS_TO_RENDER = 12;

type RefinementFacetBaseBodyProps = {
  triggerAnimation: () => void;
  onExpandFacetToggle: (code: string) => void;
  totalItemsToRender: number;
  attributes: Array<CatalogProductListFilterAttribute>;
  code: string;
  handleSelectedChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const RefinementFacetBaseBody: React.FC<RefinementFacetBaseBodyProps> = ({ triggerAnimation, totalItemsToRender, attributes, code, handleSelectedChange, onExpandFacetToggle }) => {
  useUpdateEffect(() => {
    triggerAnimation();
  }, [totalItemsToRender]);
  return (
    <>
      {attributes.slice(0, totalItemsToRender).map((item, index) => {
        return (
          <ListCheckboxRow
            key={index}
            item={item}
            handleSelectedChange={handleSelectedChange}
          />
        );
      })}
      {attributes.length > INITIAL_ITEMS_TO_RENDER && (
        <ButtonV2
          typographyVariant="button1"
          variant="ghost"
          color="black"
          onClick={() => onExpandFacetToggle(code)}
        >
          {totalItemsToRender < attributes.length ? `Show more (${attributes.length - totalItemsToRender})` : totalItemsToRender === attributes.length ? 'Show less' : null}
        </ButtonV2>
      )}
    </>
  );
};

export const RefinementFacetStringList = ({ filter, onFilterSelect, checkIsFilterGroupViewExpanded, toggleGroupFullyExpand }: RefinementFacetStringListProps) => {
  const {
    attributes,
    attributeGroup: { title, code },
  } = filter;
  const handleSelectedChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (!target.disabled && target.dataset.itemId) {
        onFilterSelect({ selectionType: ProductCatalogAttributeSelectionType.stringListMulti, filterArgs: { compositeCode: target.dataset.itemId, value: !target.checked } });
      }
    },
    [onFilterSelect],
  );

  const { totalItemsToRender } = useMemo(() => {
    const totalItemsToRender = checkIsFilterGroupViewExpanded(code) ? attributes.length : Math.min(INITIAL_ITEMS_TO_RENDER, attributes.length);
    return {
      totalItemsToRender,
    };
  }, [checkIsFilterGroupViewExpanded, code, attributes]);

  return (
    <RefinementFacetBase
      title={title}
      groupCode={code}
    >
      {({ triggerAnimation }) => (
        <RefinementFacetBaseBody
          triggerAnimation={triggerAnimation}
          totalItemsToRender={totalItemsToRender}
          attributes={attributes}
          code={code}
          handleSelectedChange={handleSelectedChange}
          onExpandFacetToggle={toggleGroupFullyExpand}
        />
      )}
    </RefinementFacetBase>
  );
};

RefinementFacetStringList.Skeleton = () => {
  return (
    <RefinementFacetBase.Skeleton>
      <ListCheckboxRowSkeleton />
      <ListCheckboxRowSkeleton />
      <ListCheckboxRowSkeleton />
    </RefinementFacetBase.Skeleton>
  );
};

RefinementFacetStringList.displayName = 'RefinementFacetStringList';
